import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupportTab({ token }) {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0 });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const fetchTickets = () => {
    const url = filter === 'all' 
      ? `${API_URL}/api/admin/support-tickets`
      : `${API_URL}/api/admin/support-tickets?status=${filter}`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        setTickets(data.tickets || []);
        setCounts(data.counts || {});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); }, [token, filter]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selected?.messages]);

  const selectTicket = (id) => {
    fetch(`${API_URL}/api/admin/support-tickets/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setSelected(data));
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    const res = await fetch(`${API_URL}/api/admin/support-tickets/${selected.id}/reply`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: reply })
    });
    if (res.ok) {
      toast.success('Sent!');
      setReply('');
      selectTicket(selected.id);
      fetchTickets();
    }
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/admin/support-tickets/${id}/status?status=${status}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    toast.success(`Marked ${status}`);
    fetchTickets();
    if (selected?.id === id) selectTicket(id);
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'in_progress', 'resolved'].map((f) => (
          <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
            onClick={() => { setFilter(f); setSelected(null); }}>
            {f === 'all' ? 'All' : f.replace('_', ' ')}
            {f === 'open' && counts.open > 0 && ` (${counts.open})`}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {tickets.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-8 text-center text-slate-400">No tickets</CardContent>
            </Card>
          ) : tickets.map((t) => (
            <Card key={t.id} onClick={() => selectTicket(t.id)}
              className={`bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 ${selected?.id === t.id ? 'ring-2 ring-blue-500' : ''}`}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium text-white text-sm truncate flex-1">{t.subject}</h3>
                  <Badge className={t.status === 'open' ? 'bg-red-500' : t.status === 'in_progress' ? 'bg-yellow-500' : 'bg-green-500'}>
                    {t.status.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">{t.name} • {t.email}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <Card className="bg-slate-800 border-slate-700 h-[500px] flex flex-col">
              <CardHeader className="border-b border-slate-700 py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white text-base">{selected.subject}</CardTitle>
                    <p className="text-sm text-slate-400">{selected.name} • {selected.email}</p>
                  </div>
                  {selected.status !== 'resolved' ? (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(selected.id, 'resolved')}>Resolve</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(selected.id, 'open')}>Reopen</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-3">
                {selected.messages?.map((m) => (
                  <div key={m.id} className={`flex ${m.from === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.from === 'admin' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-white'}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      <p className={`text-xs mt-1 ${m.from === 'admin' ? 'text-blue-200' : 'text-slate-400'}`}>
                        {new Date(m.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </CardContent>
              <div className="border-t border-slate-700 p-3 flex gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply..."
                  rows={2} className="flex-1 bg-slate-700 border-slate-600 text-white"
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) sendReply(); }} />
                <Button onClick={sendReply} disabled={!reply.trim()}>Send</Button>
              </div>
            </Card>
          ) : (
            <Card className="bg-slate-800 border-slate-700 h-[500px] flex items-center justify-center">
              <p className="text-slate-400">Select a ticket</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
